import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface ServiceSectionProps {
  id?: string;
  title: string;
  description: string;
  buttonText: string;
  buttonHref: string;
  imageUrl: string;
  imagePosition: 'left' | 'right';
  bgClass?: string;
  textColorClass?: string;
}

export default function ServiceSection({
  id,
  title,
  description,
  buttonText,
  buttonHref,
  imageUrl,
  imagePosition,
  bgClass = 'bg-stone-100',
  textColorClass = 'text-gray-900',
}: ServiceSectionProps) {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const isImageLeft = imagePosition === 'left';

  return (
    <section
      id={id}
      ref={sectionRef}
      className={`relative w-full min-h-[600px] lg:min-h-screen overflow-hidden ${bgClass}`}
    >
      <div className="flex flex-col lg:flex-row h-full min-h-[600px] lg:min-h-screen">
        {/* Image Side */}
        <div
          className={`relative w-full lg:w-1/2 h-[400px] lg:h-auto overflow-hidden ${
            isImageLeft ? 'lg:order-1' : 'lg:order-2'
          }`}
        >
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 hover:scale-105"
            style={{ backgroundImage: `url(${imageUrl})` }}
          />
          {/* Subtle overlay for cohesion */}
          <div className="absolute inset-0 bg-black/5" />
        </div>

        {/* Text Side */}
        <div
          className={`relative w-full lg:w-1/2 flex items-center ${
            isImageLeft ? 'lg:order-2' : 'lg:order-1'
          }`}
        >
          <div className="px-8 md:px-12 lg:px-20 py-16 lg:py-0 max-w-xl">
            <div
              className={`transition-all duration-1000 ${
                visible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="w-12 h-0.5 bg-gray-400 mb-6" />
              <h2
                className={`text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 ${textColorClass}`}
              >
                {title}
              </h2>
              <p
                className={`text-base md:text-lg leading-relaxed mb-8 ${
                  textColorClass === 'text-white'
                    ? 'text-white/80'
                    : 'text-gray-600'
                }`}
              >
                {description}
              </p>
              <Link
                to={buttonHref}
                className={`inline-block px-8 py-3 border text-sm tracking-widest uppercase transition-all duration-300 hover:bg-gray-900 hover:text-white hover:border-gray-900 ${
                  textColorClass === 'text-white'
                    ? 'border-white/60 text-white'
                    : 'border-gray-400 text-gray-900'
                }`}
              >
                {buttonText}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}